import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import SudanesePound from "@/components/shared/sudanese-pound";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Users, 
  ShoppingBag,
  Package,
  TrendingUp, 
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Clock,
  Activity,
  Download,
  RefreshCw,
  CreditCard,
  BarChart3,
  Star,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Tag,
  MessageSquare,
  Camera,
  ShoppingCart,
  DollarSign,
  Percent,
  FileText
} from "lucide-react";
import { cn } from "@/lib/utils";
import { adminService, type AdminDashboardStats } from "@/services/adminService";

// Unified Dashboard Component
export default function UnifiedDashboard() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingScreenshot, setSavingScreenshot] = useState(false);
  const dashboardRef = useRef<HTMLDivElement>(null);

  // Dashboard data state
  const [dashboardData, setDashboardData] = useState<AdminDashboardStats | null>(null);

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch dashboard stats from Admin API endpoint
      const response = await adminService.getDashboardStats();
      
      if (response.success && response.data) {
        setDashboardData(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch dashboard data');
      }
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || err.response?.data?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch dashboard data on mount
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Screenshot functionality
  const handleSaveScreenshot = async () => {
    if (!dashboardRef.current) return;

    try {
      setSavingScreenshot(true);
      
      // Find the scrollable parent container
      let scrollableParent = dashboardRef.current.parentElement;
      while (scrollableParent && !scrollableParent.classList.contains('overflow-y-auto')) {
        scrollableParent = scrollableParent.parentElement;
      }
      
      // Save original styles and scroll positions
      const originalOverflow = scrollableParent ? (scrollableParent as HTMLElement).style.overflow : '';
      const originalOverflowY = scrollableParent ? (scrollableParent as HTMLElement).style.overflowY : '';
      const originalHeight = scrollableParent ? (scrollableParent as HTMLElement).style.height : '';
      
      const originalScrollTop = scrollableParent 
        ? (scrollableParent as HTMLElement).scrollTop 
        : window.pageYOffset || document.documentElement.scrollTop;
      const originalScrollLeft = scrollableParent 
        ? (scrollableParent as HTMLElement).scrollLeft 
        : window.pageXOffset || document.documentElement.scrollLeft;
      
      // Temporarily remove overflow restrictions
      if (scrollableParent) {
        const parentEl = scrollableParent as HTMLElement;
        parentEl.style.overflow = 'visible';
        parentEl.style.overflowY = 'visible';
        parentEl.style.height = 'auto';
        parentEl.scrollTop = 0;
        parentEl.scrollLeft = 0;
      }
      window.scrollTo(0, 0);
      
      // Wait for fonts to load
      await document.fonts.ready;
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Get full dimensions
      const element = dashboardRef.current;
      element.offsetHeight; // Force reflow
      
      const fullWidth = element.scrollWidth || element.offsetWidth || element.clientWidth;
      const fullHeight = element.scrollHeight || element.offsetHeight || element.clientHeight;
      
      // Capture the dashboard
      const canvas = await html2canvas(element, {
        backgroundColor: '#f8fafc',
        scale: 3,
        useCORS: true,
        logging: false,
        allowTaint: false,
        foreignObjectRendering: true,
        x: 0,
        y: 0,
        width: fullWidth,
        height: fullHeight,
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
      });

      // Restore original styles
      if (scrollableParent) {
        const parentEl = scrollableParent as HTMLElement;
        parentEl.style.overflow = originalOverflow;
        parentEl.style.overflowY = originalOverflowY;
        parentEl.style.height = originalHeight;
        parentEl.scrollTop = originalScrollTop;
        parentEl.scrollLeft = originalScrollLeft;
      }
      window.scrollTo(originalScrollLeft, originalScrollTop);

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        if (!blob) {
          throw new Error('Failed to create screenshot blob');
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
        const filename = `dashboard-report-${timestamp}.png`;
        
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');
    } catch (err) {
      console.error('Error saving screenshot:', err);
      setError('Failed to save screenshot');
    } finally {
      setSavingScreenshot(false);
    }
  };

  // KPI Card Component
  const KPICard = ({ title, value, change, icon: Icon, color = 'blue', loading, subtitle }: any) => {
    const colorClasses: Record<string, string> = {
      blue: 'text-blue-600 bg-blue-50 border-blue-200',
      green: 'text-green-600 bg-green-50 border-green-200',
      purple: 'text-purple-600 bg-purple-50 border-purple-200',
      orange: 'text-orange-600 bg-orange-50 border-orange-200',
      red: 'text-red-600 bg-red-50 border-red-200'
    };

    const trendIcons: Record<string, any> = {
      up: ArrowUpRight,
      down: ArrowDownRight,
      stable: Minus
    };

    const trendColors: Record<string, string> = {
      up: 'text-green-600 bg-green-100',
      down: 'text-red-600 bg-red-100',
      stable: 'text-gray-600 bg-gray-100'
    };

    const TrendIcon = change ? trendIcons[change.trend] : Minus;

    return (
      <Card className="relative overflow-hidden transition-all duration-200 hover:shadow-lg border-0 shadow-sm">
        <div className={cn("absolute top-0 left-0 w-full h-1", colorClasses[color]?.split(' ')[2] || 'border-blue-200')} />
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {title}
          </CardTitle>
          <div className={cn("p-2 rounded-lg", colorClasses[color] || colorClasses.blue)}>
            <Icon className="h-4 w-4" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="text-2xl font-bold tracking-tight">
              {loading ? <Skeleton className="h-8 w-24" /> : value}
            </div>
            {subtitle && (
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            )}
            {change && !loading && (
              <div className="flex items-center space-x-2">
                <div className={cn("flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium", trendColors[change.trend] || trendColors.stable)}>
                  <TrendIcon className="h-3 w-3" />
                  <span>{Math.abs(change.rate).toFixed(1)}%</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {t('vsPreviousPeriod') || 'vs previous period'}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Format uptime helper
  const formatUptime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  return (
    <div ref={dashboardRef} className="min-h-screen bg-gradient-to-br from-slate-50/50 to-slate-100/50" dir={t('language') === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 sm:p-6">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground truncate">
            {t('dashboard') || 'Dashboard'}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1 line-clamp-2">
            {t('welcomeBackMessage') || "Welcome back! Here's what's happening with your e-commerce business today."}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchDashboardData}
              disabled={loading}
              className="gap-2 flex-1 sm:flex-none"
            >
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
              <span className="hidden sm:inline">{t('refresh') || 'Refresh'}</span>
            </Button>

            <Button
              variant="default"
              size="sm"
              onClick={handleSaveScreenshot}
              disabled={savingScreenshot}
              className="gap-2 flex-1 sm:flex-none"
            >
              <Camera className={cn("h-4 w-4", savingScreenshot && "animate-pulse")} />
              <span className="hidden sm:inline">
                {savingScreenshot ? (t('savingScreenshot') || 'Saving...') : (t('saveScreenshot') || 'Save Screenshot')}
              </span>
            </Button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="px-4 sm:px-6">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      )}

      {/* Main Content - All sections on one page */}
      <div className="space-y-6 p-4 sm:p-6">
        {/* Overview KPIs */}
        <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <KPICard
            title={t('totalRevenue') || 'Total Revenue'}
            value={dashboardData ? (
              <SudanesePound amount={dashboardData.overview?.total_revenue || 0} variant="new" />
            ) : (
              <SudanesePound amount={0} variant="new" />
            )}
            change={dashboardData?.growth_metrics?.revenue_growth ? {
              rate: dashboardData.growth_metrics.revenue_growth.growth_rate || 0,
              trend: (dashboardData.growth_metrics.revenue_growth.growth_rate || 0) > 0 ? 'up' : 'down',
              period: 'previous period'
            } : undefined}
            icon={CreditCard}
            color="green"
            loading={loading}
            subtitle={t('totalEarnings') || 'Total earnings'}
          />
          
          <KPICard
            title={t('totalUsers') || 'Total Users'}
            value={dashboardData?.overview?.total_users || 0}
            change={dashboardData?.growth_metrics?.user_growth ? {
              rate: dashboardData.growth_metrics.user_growth.growth_rate || 0,
              trend: (dashboardData.growth_metrics.user_growth.growth_rate || 0) > 0 ? 'up' : 'down',
              period: 'previous period'
            } : undefined}
            icon={Users}
            color="purple"
            loading={loading}
            subtitle={t('registeredCustomers') || 'Registered customers'}
          />
          
          <KPICard
            title={t('totalOrders') || 'Total Orders'}
            value={dashboardData?.overview?.total_orders || 0}
            change={dashboardData?.growth_metrics?.order_growth ? {
              rate: dashboardData.growth_metrics.order_growth.growth_rate || 0,
              trend: (dashboardData.growth_metrics.order_growth.growth_rate || 0) > 0 ? 'up' : 'down',
              period: 'previous period'
            } : undefined}
            icon={ShoppingCart}
            color="blue"
            loading={loading}
            subtitle={t('allOrders') || 'All orders'}
          />
          
          <KPICard
            title={t('totalProducts') || 'Total Products'}
            value={dashboardData?.overview?.total_products || 0}
            icon={Package}
            color="orange"
            loading={loading}
            subtitle={t('productsInCatalog') || 'Products in catalog'}
          />
        </div>

        {/* Secondary KPIs */}
        <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <KPICard
            title={t('activeOrders') || 'Active Orders'}
            value={dashboardData?.overview?.active_orders || 0}
            icon={Activity}
            color="blue"
            loading={loading}
          />
          
          <KPICard
            title={t('pendingReviews') || 'Pending Reviews'}
            value={dashboardData?.overview?.pending_reviews || 0}
            icon={MessageSquare}
            color="orange"
            loading={loading}
          />

          <KPICard
            title={t('systemUptime') || 'System Uptime'}
            value={dashboardData?.overview?.system_uptime ? formatUptime(dashboardData.overview.system_uptime) : '0m'}
            icon={Clock}
            color="green"
            loading={loading}
          />

          <KPICard
            title={t('lastUpdate') || 'Last Update'}
            value={dashboardData?.overview?.last_update ? formatDate(dashboardData.overview.last_update) : 'N/A'}
            icon={Clock}
            color="purple"
            loading={loading}
          />
        </div>

        {/* Growth Metrics */}
        {dashboardData?.growth_metrics && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                {t('growthMetrics') || 'Growth Metrics'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                {/* User Growth */}
                {dashboardData.growth_metrics.user_growth && (
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-purple-600" />
                        <span className="font-medium">{t('userGrowth') || 'User Growth'}</span>
                      </div>
                      <div className={`flex items-center gap-1 ${
                        (dashboardData.growth_metrics.user_growth.growth_rate || 0) > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {(dashboardData.growth_metrics.user_growth.growth_rate || 0) > 0 ? (
                          <TrendingUp className="h-4 w-4" />
                        ) : (
                          <TrendingDown className="h-4 w-4" />
                        )}
                        <span className="font-semibold">
                          {Math.abs(dashboardData.growth_metrics.user_growth.growth_rate || 0).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">{t('current') || 'Current'}: </span>
                        <span className="font-semibold">{dashboardData.growth_metrics.user_growth.current_period || 0}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">{t('previous') || 'Previous'}: </span>
                        <span className="font-semibold">{dashboardData.growth_metrics.user_growth.previous_period || 0}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Order Growth */}
                {dashboardData.growth_metrics.order_growth && (
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <ShoppingCart className="h-4 w-4 text-blue-600" />
                        <span className="font-medium">{t('orderGrowth') || 'Order Growth'}</span>
                      </div>
                      <div className={`flex items-center gap-1 ${
                        (dashboardData.growth_metrics.order_growth.growth_rate || 0) > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {(dashboardData.growth_metrics.order_growth.growth_rate || 0) > 0 ? (
                          <TrendingUp className="h-4 w-4" />
                        ) : (
                          <TrendingDown className="h-4 w-4" />
                        )}
                        <span className="font-semibold">
                          {Math.abs(dashboardData.growth_metrics.order_growth.growth_rate || 0).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">{t('current') || 'Current'}: </span>
                        <span className="font-semibold">{dashboardData.growth_metrics.order_growth.current_period || 0}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">{t('previous') || 'Previous'}: </span>
                        <span className="font-semibold">{dashboardData.growth_metrics.order_growth.previous_period || 0}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Revenue Growth */}
                {dashboardData.growth_metrics.revenue_growth && (
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-green-600" />
                        <span className="font-medium">{t('revenueGrowth') || 'Revenue Growth'}</span>
                      </div>
                      <div className={`flex items-center gap-1 ${
                        (dashboardData.growth_metrics.revenue_growth.growth_rate || 0) > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {(dashboardData.growth_metrics.revenue_growth.growth_rate || 0) > 0 ? (
                          <TrendingUp className="h-4 w-4" />
                        ) : (
                          <TrendingDown className="h-4 w-4" />
                        )}
                        <span className="font-semibold">
                          {Math.abs(dashboardData.growth_metrics.revenue_growth.growth_rate || 0).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">{t('current') || 'Current'}: </span>
                        <span className="font-semibold">
                          <SudanesePound amount={dashboardData.growth_metrics.revenue_growth.current_period || 0} variant="new" />
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">{t('previous') || 'Previous'}: </span>
                        <span className="font-semibold">
                          <SudanesePound amount={dashboardData.growth_metrics.revenue_growth.previous_period || 0} variant="new" />
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Financial Summary & Operational Metrics */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Financial Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                {t('financialSummary') || 'Financial Summary'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[200px] w-full" />
              ) : dashboardData?.financial_summary ? (
                <div className="space-y-4">
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="p-4 border rounded-lg bg-gradient-to-br from-green-50 to-green-100">
                      <div className="text-sm text-green-700 font-medium mb-1">{t('totalRevenue') || 'Total Revenue'}</div>
                      <div className="text-xl font-bold text-green-600">
                        <SudanesePound amount={dashboardData.financial_summary.total_revenue || 0} variant="new" />
                      </div>
                    </div>
                    <div className="p-4 border rounded-lg bg-gradient-to-br from-yellow-50 to-yellow-100">
                      <div className="text-sm text-yellow-700 font-medium mb-1">{t('pendingPayments') || 'Pending Payments'}</div>
                      <div className="text-xl font-bold text-yellow-600">
                        <SudanesePound amount={dashboardData.financial_summary.pending_payments || 0} variant="new" />
                      </div>
                    </div>
                    <div className="p-4 border rounded-lg bg-gradient-to-br from-red-50 to-red-100">
                      <div className="text-sm text-red-700 font-medium mb-1">{t('refundsIssued') || 'Refunds Issued'}</div>
                      <div className="text-xl font-bold text-red-600">
                        <SudanesePound amount={dashboardData.financial_summary.refunds_issued || 0} variant="new" />
                      </div>
                    </div>
                    <div className="p-4 border rounded-lg bg-gradient-to-br from-blue-50 to-blue-100">
                      <div className="text-sm text-blue-700 font-medium mb-1">{t('commissionEarned') || 'Commission Earned'}</div>
                      <div className="text-xl font-bold text-blue-600">
                        <SudanesePound amount={dashboardData.financial_summary.commission_earned || 0} variant="new" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Payment Methods */}
                  {dashboardData.financial_summary.payment_methods && Object.keys(dashboardData.financial_summary.payment_methods).length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-semibold text-sm mb-3">{t('paymentMethods') || 'Payment Methods'}</h4>
                      <div className="space-y-2">
                        {Object.entries(dashboardData.financial_summary.payment_methods).map(([method, data]: [string, any]) => (
                          <div key={method} className="flex items-center justify-between p-2 border rounded">
                            <span className="text-sm font-medium capitalize">{method.replace('_', ' ')}</span>
                            <div className="text-right">
                              <div className="text-sm font-semibold">
                                <SudanesePound amount={data.total || 0} variant="new" />
                              </div>
                              <div className="text-xs text-muted-foreground">{data.count || 0} {t('transactions') || 'transactions'}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <CreditCard className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t('noDataAvailable') || 'No Data Available'}</h3>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Operational Metrics */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Activity className="h-5 w-5" />
                {t('operationalMetrics') || 'Operational Metrics'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[200px] w-full" />
              ) : dashboardData?.operational_metrics ? (
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="p-4 border rounded-lg">
                      <div className="text-sm text-muted-foreground mb-1">{t('averageOrderValue') || 'Average Order Value'}</div>
                      <div className="text-2xl font-bold">
                        <SudanesePound amount={dashboardData.operational_metrics.average_order_value || 0} variant="new" />
                      </div>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="text-sm text-muted-foreground mb-1">{t('cancellationRate') || 'Cancellation Rate'}</div>
                      <div className="text-2xl font-bold text-red-600">
                        {(dashboardData.operational_metrics.cancellation_rate || 0).toFixed(1)}%
                      </div>
                    </div>
                  </div>
                  
                  {/* Orders by Status */}
                  {dashboardData.operational_metrics.total_orders_by_status && Object.keys(dashboardData.operational_metrics.total_orders_by_status).length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-semibold text-sm mb-3">{t('ordersByStatus') || 'Orders by Status'}</h4>
                      <div className="space-y-2">
                        {Object.entries(dashboardData.operational_metrics.total_orders_by_status).map(([status, data]: [string, any]) => (
                          <div key={status} className="flex items-center justify-between p-2 border rounded">
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${
                                status === 'paid' ? 'bg-green-500' :
                                status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'
                              }`} />
                              <span className="text-sm font-medium capitalize">{status}</span>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold">{data.count || 0}</div>
                              <div className="text-xs text-muted-foreground">
                                <SudanesePound amount={data.avg_amount || 0} variant="new" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <Activity className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t('noDataAvailable') || 'No Data Available'}</h3>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* User Metrics & Product Metrics */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* User Metrics */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t('userMetrics') || 'User Metrics'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[200px] w-full" />
              ) : dashboardData?.user_metrics?.by_role && dashboardData.user_metrics.by_role.length > 0 ? (
                <div className="space-y-3">
                  {dashboardData.user_metrics.by_role.map((role, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <div className="font-medium capitalize">{role.role}</div>
                        <div className="text-xs text-muted-foreground">
                          {t('lastSignup') || 'Last signup'}: {formatDate(role.last_signup)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold">{role.count}</div>
                        <div className="text-xs text-muted-foreground">{t('users') || 'users'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <Users className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t('noDataAvailable') || 'No Data Available'}</h3>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Product Metrics */}
          {dashboardData?.product_metrics && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  {t('productMetrics') || 'Product Metrics'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[200px] w-full" />
                ) : (
                  <div className="space-y-4">
                    {/* By Category */}
                    {dashboardData.product_metrics.by_category && dashboardData.product_metrics.by_category.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm mb-3">{t('byCategory') || 'By Category'}</h4>
                        <div className="space-y-2">
                          {dashboardData.product_metrics.by_category.map((category, index) => (
                            <div key={index} className="flex items-center justify-between p-2 border rounded">
                              <div>
                                <div className="font-medium">{category.category}</div>
                                <div className="text-xs text-muted-foreground">{category.category_ar}</div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-semibold">{category.count}</div>
                                <div className="text-xs text-muted-foreground">
                                  <SudanesePound amount={category.avg_price || 0} variant="new" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* By Brand */}
                    {dashboardData.product_metrics.by_brand && dashboardData.product_metrics.by_brand.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="font-semibold text-sm mb-3">{t('byBrand') || 'By Brand'}</h4>
                        <div className="space-y-2">
                          {dashboardData.product_metrics.by_brand.map((brand, index) => (
                            <div key={index} className="flex items-center justify-between p-2 border rounded">
                              <div>
                                <div className="font-medium">{brand.brand}</div>
                                <div className="text-xs text-muted-foreground">{brand.brand_ar}</div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-semibold">{brand.count}</div>
                                <div className="text-xs text-muted-foreground">
                                  <SudanesePound amount={brand.avg_price || 0} variant="new" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Top Performers & Review Metrics */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Top Performers */}
          {dashboardData?.product_metrics?.top_performers && dashboardData.product_metrics.top_performers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Star className="h-5 w-5" />
                  {t('topPerformers') || 'Top Performers'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData.product_metrics.top_performers.map((product, index) => (
                    <div key={product.id || index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-full">
                          <span className="text-sm font-semibold text-primary">#{index + 1}</span>
                        </div>
                        <div>
                          <div className="font-medium">{product.name || 'N/A'}</div>
                          <div className="text-xs text-muted-foreground">
                            {product.booking_count || 0} {t('orders') || 'orders'}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-green-600">
                          <SudanesePound amount={product.total_revenue || 0} variant="new" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Review Metrics */}
          {dashboardData?.review_metrics && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Star className="h-5 w-5" />
                  {t('reviewMetrics') || 'Review Metrics'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center p-4 border rounded-lg">
                    <div className="text-3xl font-bold text-yellow-600 mb-2 flex items-center justify-center gap-2">
                      <Star className="h-8 w-8 fill-yellow-500" />
                      {dashboardData.review_metrics.average_rating?.toFixed(1) || 0}
                    </div>
                    <div className="text-sm text-muted-foreground font-medium">{t('averageRating') || 'Average Rating'}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-center p-3 border rounded">
                      <div className="text-xl font-bold text-blue-600">{dashboardData.review_metrics.total_reviews || 0}</div>
                      <div className="text-xs text-muted-foreground">{t('totalReviews') || 'Total'}</div>
                    </div>
                    <div className="text-center p-3 border rounded">
                      <div className="text-xl font-bold text-green-600">{dashboardData.review_metrics.positive_reviews || 0}</div>
                      <div className="text-xs text-muted-foreground">{t('positive') || 'Positive'}</div>
                    </div>
                    <div className="text-center p-3 border rounded">
                      <div className="text-xl font-bold text-red-600">{dashboardData.review_metrics.negative_reviews || 0}</div>
                      <div className="text-xs text-muted-foreground">{t('negative') || 'Negative'}</div>
                    </div>
                    <div className="text-center p-3 border rounded">
                      <div className="text-xl font-bold text-orange-600">{dashboardData.review_metrics.satisfaction_rate?.toFixed(1) || 0}%</div>
                      <div className="text-xs text-muted-foreground">{t('satisfaction') || 'Satisfaction'}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Payment Metrics */}
        {dashboardData?.payment_metrics && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                {t('paymentMetrics') || 'Payment Metrics'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
                <div className="text-center p-4 border rounded-lg bg-gradient-to-br from-green-50 to-green-100">
                  <div className="text-2xl font-bold text-green-600 mb-2">
                    {dashboardData.payment_metrics.successful_payments || 0}
                  </div>
                  <div className="text-sm text-green-700 font-medium">{t('successfulPayments') || 'Successful Payments'}</div>
                </div>
                <div className="text-center p-4 border rounded-lg bg-gradient-to-br from-yellow-50 to-yellow-100">
                  <div className="text-2xl font-bold text-yellow-600 mb-2">
                    {dashboardData.payment_metrics.pending_payments || 0}
                  </div>
                  <div className="text-sm text-yellow-700 font-medium">{t('pendingPayments') || 'Pending Payments'}</div>
                </div>
                <div className="text-center p-4 border rounded-lg bg-gradient-to-br from-red-50 to-red-100">
                  <div className="text-2xl font-bold text-red-600 mb-2">
                    {dashboardData.payment_metrics.failed_payments || 0}
                  </div>
                  <div className="text-sm text-red-700 font-medium">{t('failedPayments') || 'Failed Payments'}</div>
                </div>
                <div className="text-center p-4 border rounded-lg bg-gradient-to-br from-blue-50 to-blue-100">
                  <div className="text-2xl font-bold text-blue-600 mb-2">
                    {dashboardData.payment_metrics.payment_success_rate?.toFixed(1) || 0}%
                  </div>
                  <div className="text-sm text-blue-700 font-medium">{t('successRate') || 'Success Rate'}</div>
                </div>
              </div>

              {/* Payment Providers */}
              {dashboardData.payment_metrics.by_provider && dashboardData.payment_metrics.by_provider.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm mb-3">{t('paymentProviders') || 'Payment Providers'}</h4>
                  {dashboardData.payment_metrics.by_provider.map((provider, index) => (
                    <div key={index} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="font-medium">{provider.provider_name || provider.provider_code || 'Unknown'}</div>
                          <div className="text-xs text-muted-foreground">
                            {provider.successful_transactions || 0} / {provider.total_transactions || 0} {t('transactions') || 'transactions'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold">{provider.success_rate?.toFixed(1) || 0}%</div>
                          <div className="text-xs text-muted-foreground">
                            <SudanesePound amount={provider.successful_amount || 0} variant="new" />
                          </div>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full"
                          style={{ width: `${provider.success_rate || 0}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Recent Activity */}
        {dashboardData?.recent_activity?.latest_orders && dashboardData.recent_activity.latest_orders.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Activity className="h-5 w-5" />
                {t('recentActivity') || 'Recent Activity'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dashboardData.recent_activity.latest_orders.map((order) => (
                  <div key={order.order_id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors border">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-full">
                        <ShoppingCart className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">
                            Order #{order.order_id.substring(0, 8)}...
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {order.user?.name || order.user?.email || 'Unknown User'}
                          </p>
                          {order.products && order.products.length > 0 && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {order.products.length} {t('product') || 'product'}(s)
                            </div>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <span className="text-sm font-semibold text-green-600 block">
                            <SudanesePound amount={order.amount || 0} variant="new" />
                          </span>
                          <Badge variant="secondary" className={cn("text-xs mt-1", 
                            order.status === 'paid' ? 'bg-green-100 text-green-800' :
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          )}>
                            {order.status}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-muted-foreground">
                          {formatDate(order.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
